import { Input } from '@globalmed/atlas-ui';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './POCTab.module.css';
import { TCreateOrganizationForm } from '../../schema';
import { LocationFields } from '@/components/LocationFields';
import { PhoneField } from '@/components/PhoneField';

/**
 * POC tab of the create organization modal.
 * @returns JSX.Element representing the POC tab.
 */
export const POCTab = () => {
  const {
    control,
    formState: { isSubmitting },
  } = useFormContext<TCreateOrganizationForm>();
  const { t } = useTranslation();

  return (
    <div className={styles.flexColumn}>
      <Controller
        name="pocSettings.name"
        control={control}
        render={({ field, fieldState }) => (
          <Input
            label={t('common.name')}
            placeholder={t('admin-panel.pocNamePlaceholder')}
            width="100%"
            disabled={isSubmitting}
            {...field}
            isError={!!fieldState.error}
            message={t(fieldState.error?.message ?? '')}
            required
          />
        )}
      />

      <Controller
        name="pocSettings.title"
        control={control}
        render={({ field, fieldState }) => (
          <Input
            label={t('admin-panel.pocTitleLabel')}
            placeholder={t('admin-panel.pocTitlePlaceholder')}
            width="100%"
            disabled={isSubmitting}
            {...field}
            isError={!!fieldState.error}
            message={t(fieldState.error?.message ?? '')}
            required
          />
        )}
      />

      <Controller
        name="pocSettings.email"
        control={control}
        render={({ field, fieldState }) => (
          <Input
            label={t('common.email')}
            placeholder={t('admin-panel.pocEmailPlaceholder')}
            width="100%"
            disabled={isSubmitting}
            {...field}
            isError={!!fieldState.error}
            message={t(fieldState.error?.message ?? '')}
            required
          />
        )}
      />

      <PhoneField
        name="pocSettings.phone"
        sx={{ width: '100%' }}
        disabled={isSubmitting}
      />

      <div className={styles.locationWrap}>
        <LocationFields
          nameCountry="pocSettings.country"
          nameState="pocSettings.state"
          stateLabel="common.state-territory"
        />
      </div>

      <div className={styles.block}>
        <Controller
          name="pocSettings.address1"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              label={t('create-organization-modal.address-line-1')}
              placeholder={t(
                'create-organization-modal.address-line-1-placeholder',
              )}
              width="100%"
              disabled={isSubmitting}
              {...field}
              value={field.value ?? ''}
              isError={!!fieldState.error?.message}
              message={t(fieldState.error?.message ?? '')}
            />
          )}
        />

        <Controller
          name="pocSettings.address2"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              label={t('create-organization-modal.address-line-2')}
              placeholder={t(
                'create-organization-modal.address-line-2-placeholder',
              )}
              width="100%"
              disabled={isSubmitting}
              {...field}
              value={field.value ?? ''}
              isError={!!fieldState.error?.message}
              message={t(fieldState.error?.message ?? '')}
            />
          )}
        />
      </div>

      <Controller
        name="pocSettings.zip"
        control={control}
        render={({ field, fieldState }) => (
          <Input
            label={t('common.zipCodeLabel')}
            placeholder={t('common.zipCodePlaceholder')}
            width="100%"
            disabled={isSubmitting}
            {...field}
            value={field.value ?? ''}
            isError={!!fieldState.error?.message}
            message={t(fieldState.error?.message ?? '')}
          />
        )}
      />
    </div>
  );
};
